import { ThemeProvider } from '@emotion/react';

import * as React from 'react';

import PropTypes from 'prop-types';
import { compose } from 'recompose';

import Retracked from 'js/app/retracked';
import connectToRouter from 'js/lib/connectToRouter';
import connectToStores from 'js/lib/connectToStores';

import withCCPA from 'bundles/marketing-consent/components/withCCPA';
import withGDPR from 'bundles/marketing-consent/components/withGDPR';
import PageFooter from 'bundles/page-footer/components/PageFooter';
import PageHeader from 'bundles/page-header/components/PageHeader';
import withFetchLazyImages from 'bundles/page/components/shared/utils/withFetchLazyImages';
import { IS_PART_OF_COURSERA_PLUS } from 'bundles/search-common/constants/filter';
import LearnerProvider, { useShowCourseraTier } from 'bundles/search-common/providers/LearnerProvider';
import RouterProvider from 'bundles/search-common/providers/RouterProvider';
import SearchProvider from 'bundles/search-common/providers/SearchProvider';
import { searchTheme } from 'bundles/search-common/searchTheme';
import { useSearchConfigs } from 'bundles/search-v2/hooks/useSearchConfigs';
import ApplicationStoreClass from 'bundles/ssr/stores/ApplicationStore';
import inServerContext from 'bundles/ssr/util/inServerContext';

import 'css!bundles/search-common/index';

type InputProps = {};

type Props = InputProps & {
  csrfToken?: string;
  requestCountryCode?: string;
  hasCourseraPlusSearchFilterParam: boolean;
};

export const SearchProviderWrapper = ({
  hasCourseraPlusSearchFilterParam,
  children,
}: {
  hasCourseraPlusSearchFilterParam: boolean;
  children: React.ReactNode;
}) => {
  const userHasCourseraPlus = useShowCourseraTier();
  const algoliaIndexConfigs = useSearchConfigs(hasCourseraPlusSearchFilterParam || userHasCourseraPlus);

  return <SearchProvider searchConfigs={algoliaIndexConfigs}>{children}</SearchProvider>;
};

class SearchApp extends React.Component<Props> {
  static propTypes = {
    requestCountryCode: PropTypes.string,
    csrfToken: PropTypes.string,
    children: PropTypes.node,
  };

  static contextTypes = {
    router: PropTypes.object.isRequired,
    getStore: PropTypes.func.isRequired,
  };

  static childContextTypes = {
    csrfToken: PropTypes.string,
    requestCountryCode: PropTypes.string,
  };

  getChildContext() {
    const { csrfToken, requestCountryCode } = this.props;
    return { csrfToken, requestCountryCode };
  }

  render() {
    const { children, hasCourseraPlusSearchFilterParam } = this.props;
    // used to prevent browser from scrolling to the last scroll position when navigating back/refreshing.
    // needed due to infinite scroll implementation not handling scroll restoration. fixable with virtual scroll.
    if (!inServerContext && 'scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
    return (
      <div className="rc-SearchApp">
        <PageHeader showShoppingCart showGDPRBanner isSearchPage showLanguagesDropdown />
        <ThemeProvider theme={searchTheme}>
          <RouterProvider>
            <LearnerProvider>
              <SearchProviderWrapper hasCourseraPlusSearchFilterParam={hasCourseraPlusSearchFilterParam}>
                {children}
              </SearchProviderWrapper>
            </LearnerProvider>
          </RouterProvider>
        </ThemeProvider>
        <PageFooter />
      </div>
    );
  }
}

export default compose<Props, InputProps>(
  connectToStores([ApplicationStoreClass], (ApplicationStore, props: InputProps) => ({
    ...props,
    csrfToken: ApplicationStore.getState().csrfToken,
    requestCountryCode: ApplicationStore.getState().requestCountryCode,
  })),
  Retracked.createTrackedContainer(() => ({
    namespace: {
      app: 'search',
      page: 'search',
    },
  })),
  withFetchLazyImages,
  withGDPR,
  withCCPA,
  connectToRouter((router) => ({
    hasCourseraPlusSearchFilterParam: router?.location?.query?.[IS_PART_OF_COURSERA_PLUS] === 'true',
  }))
)(SearchApp);
